import React, { useEffect } from 'react';
import { Button, Form, message, Spin } from 'antd';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ExclamationCircleOutlined, LoadingOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';

import ContentHeader from '../../../Common/ContentHeader';
import { useLayoutContext } from '../../../Layout/context';
import { articleCreate as articleCreateAction,
  articleCreateClear as articleCreateClearAction,
  articleCreateErrorClear as articleCreateErrorClearAction } from '../../../../store/article/create';
import { ArticleStatus, useArticle } from '../../../../store/article';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { InputFileDragger } from '../../../Common/InputFileDragger';
import { SelectArticleTags } from '../../../Common/SelectArticleTags';
import { useTagsAll } from '../../../../hooks/useTagsAll';
import { InputName } from '../../../Common/InputName';
import InputRichDescription from '../../../Common/InputRichDescription';
import InputVideoDragger from '../../../Common/InputVideoDragger';
import ArticleAuthorFields from '../AuthorFields';
import { getArticleInnerMenuItems, useArticleTypeParam } from '../helpers';
import { ArticleTypeDisplayName } from '../types';
import SeoForm from '../../../Common/SeoForm';
import { Seo } from '../../../../types/seo';
import { onFormFinishFailed } from '../../../../utils/form';

import styles from './index.module.scss';

interface ArticleCreateContentParams {
  articleCreate: typeof articleCreateAction;
  articleCreateClear: typeof articleCreateClearAction;
  articleCreateErrorClear: typeof articleCreateErrorClearAction;
}

export const seoInitialValues: Seo = {
  metaTitle: '',
  keywords: undefined,
  metaDescription: '',
  isIndexed: false,
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
};

export interface ArticleCreateFormValues {
  name: string;
  description: string;
  fileId: string;
  videoId: string | null;
  status: ArticleStatus;
  tags: string[];

  authorImageId: string | null;
  authorName: string;
  authorDescription: string;
  slug: string;
  seo: Seo;
}

const initialValues: ArticleCreateFormValues = {
  name: '',
  description: '',
  fileId: '',
  videoId: '',
  status: 'draft',
  tags: [],

  authorImageId: '',
  authorName: '',
  authorDescription: '',
  slug: '',
  seo: seoInitialValues,
};

function ArticleCreateContent({
  articleCreate,
  articleCreateClear,
  articleCreateErrorClear,
}: ArticleCreateContentParams) {
  const { articleType } = useArticleTypeParam();
  const [form] = Form.useForm<ArticleCreateFormValues>();
  const { setBreadcrumbs } = useLayoutContext();
  const { create } = useArticle();
  const navigate = useNavigate();
  const { modal, contextHolder } = useSimpleModal();
  const { options, tags: tagsAll } = useTagsAll();

  const statusValue = form.getFieldValue('status');
  const isDtaftLoading = statusValue === 'draft' && create.loading;
  const isPublishLoading = statusValue === 'published' && create.loading;

  const onFinish = ({ tags, ...values }: ArticleCreateFormValues) => {
    form.validateFields(Object.keys(initialValues)).then(() => {
      articleCreate({
        ...values,
        videoId: values.videoId || null,
        authorImageId: values.authorImageId || null,
        tags: tags.map(
          (tagFormValue: string) => tagsAll.find((tag) => tag.id === tagFormValue) || { name: tagFormValue },
        ),
        articleType, /** Required param to define in which section article will be shown */
      });
    });
  };

  useEffect(() => {
    setBreadcrumbs([...getArticleInnerMenuItems(), { title: 'Create' }]);
  }, []);

  useEffect(() => {
    if (create.response) {
      message.success('Successfully created').then(() => {
        articleCreateClear();
        navigate(`/articles/${articleType}/${create.response?.id}`);
      });
    }
  }, [create.response]);

  useEffect(() => {
    if (create.error) {
      message.error(
        create.error.data.message === 'Error unique column'
          ? 'Article with this name already exist'
          : create.error.data.message,
      ).then(articleCreateErrorClear);
    }
  }, [create.error]);

  useEffect(
    () => () => {
      articleCreateClear();
      articleCreateErrorClear();
    },
    [],
  );

  const onCancel = () => {
    const { status, seo, ...fieldsToCheckEmpty } = form.getFieldsValue();

    const hasFilledFields = Object.values(fieldsToCheckEmpty).some(
      (v) => (typeof v === 'string' || Array.isArray(v)) && v.length > 0,
    );
    const hasFilledSeo = seo && Object.values(seo).some(
      (v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)),
    );

    if (hasFilledFields || hasFilledSeo) {
      modal.confirm({
        title: 'Are you sure you want to cancel the article creation?',
        icon: <ExclamationCircleOutlined />,
        content: 'Changes will not be saved.',
        onOk: () => navigate(`/articles/${articleType}`),
      });
    } else {
      navigate(`/articles/${articleType}`);
    }
  };

  const onSaveDraft = () => {
    form.setFieldValue('status', 'draft');
    form.submit();
  };

  const onSaveAndPublish = () => {
    form.setFieldValue('status', 'published');
    form.submit();
  };

  useEffect(() => {
    if (create.response) {
      message.success('Successfully updated').then(() => navigate(`/articles/${articleType}/${create.response?.id}`));
    }
  }, [create.response]);

  return (
    <div>
      <ContentHeader title={`${ArticleTypeDisplayName[articleType]} Article Create`}>
        <div className={styles.actions}>
          <Button onClick={onCancel} loading={create.loading}>
            Cancel
          </Button>
          {contextHolder}
          <Button
            type="primary"
            onClick={onSaveDraft}
            loading={isDtaftLoading}
            icon={isDtaftLoading ? <LoadingOutlined /> : <SaveOutlined />}
          >
            Save as draft
          </Button>
          <Button
            type="primary"
            onClick={onSaveAndPublish}
            loading={isPublishLoading}
            icon={isPublishLoading ? <LoadingOutlined /> : <UploadOutlined />}
          >
            Publish
          </Button>
        </div>
      </ContentHeader>

      <Spin spinning={create.loading} indicator={<LoadingOutlined spin />}>
        <Form<ArticleCreateFormValues>
          onFinish={onFinish}
          onFinishFailed={onFormFinishFailed}
          form={form}
          className={styles.form}
          layout="vertical"
          initialValues={initialValues}
        >
          <div className={styles.smallContainer}>
            <InputFileDragger
              form={form}
              name="fileId"
              rules={[{ required: true, message: 'Please upload an image' }]}
              allowDelete
            />
            <InputVideoDragger form={form} name="videoId" />
            <InputName formItemProps={{ label: 'Header', rules: [{ required: true }] }} />
          </div>

          {/* TODO upd description here */}
          <InputRichDescription />
          <Form.Item name="status" hidden />
          <div className={styles.smallContainer}>
            <SelectArticleTags options={options} mode="tags" />
          </div>

          <ArticleAuthorFields form={form} />

          <SeoForm form={form} initialValues={{ seo: initialValues.seo, slug: initialValues.slug }} />
        </Form>
      </Spin>
    </div>
  );
}

export default connect(undefined, {
  articleCreate: articleCreateAction,
  articleCreateClear: articleCreateClearAction,
  articleCreateErrorClear: articleCreateErrorClearAction,
})(ArticleCreateContent);
